import { DieManagement_DieMasterTourSteps } from "../../components/TourGuide/tourGuideData";
import { useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose, MdAdd } from "react-icons/md";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import ConfirmDeleteModal from "../../common/DeleteModel";
import { dieMasterAPI } from "../../utlis/apiService";
import DieForm from "./DieForm";
import ErrorModal from "../../common/CustomModel";
import Button from "../../common/Button";
import { formatDate } from "../../utlis/formateDate";
import { STATUS_COLORS } from "../../utlis/constant";
import { CalculationFooter, RegistryHeader, Button as SharedButton } from "../../common/Shared";
import { saveAs } from "file-saver";
import { MdDownload } from "react-icons/md";
import { HiOutlinePlus } from "react-icons/hi";
import { TourGuide } from "../../components/TourGuide/TourGuide";
import { Info } from "lucide-react";

const blankDieForm = {
  id: 0,
  dieNumber: "",
  dieDate: new Date().toISOString()?.split("T")[0],
  subDieNo: "",
  invoiceNo: "",
  profileName: "",
  seriesName: "",
  customerName: "",
  widthHeight: "",
  currentWeight: "",
  orderWeight: "",
  status_Id: "",
  supplier_Id: "",
  location_Id: "",
  range: "",
  die_Pdf: null,
  die_Image: null,
  created_By: 1, // Defaulting if userId missing, but typically handled by session
  toolingSize_Id: "",
  die_Type_Id: "",
  no_Of_Cavity: "",
  hardness: "",
  area_Sqmm: "",
  perimeter: "",
  profileThickness: "",
};

const DieMaster = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const today = new Date().toISOString()?.split("T")[0];

  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState(blankDieForm);
  const [loading, setLoading] = useState(false);
  const [isDieMasterData, setIsDieMasterData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedIds, setSelectedIds] = useState([]);

  const [isTourOpen, setIsTourOpen] = useState(false);

  useEffect(() => {
    const hasSeenTour = localStorage.getItem("aluex_diemaster_tour_seen");
    if (!hasSeenTour) {
      const timer = setTimeout(() => {
        setIsTourOpen(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleTourComplete = () => {
    localStorage.setItem("aluex_diemaster_tour_seen", "true");
  };



  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleToggle = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const handleToggleAll = (ids, checked) => {
    setSelectedIds((prev) => {
      if (checked) {
        const newIds = ids.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      } else {
        return prev.filter((id) => !ids.includes(id));
      }
    });
  };

  const handleDownloadPDFs = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      const data = await dieMasterAPI.mergePDFs(selectedIds);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Dies_Merged_${new Date().getTime()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      notify("PDF downloaded successfully", "success");
    } catch (error) {
      notify("Failed to merge and download PDFs", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredData = isDieMasterData.filter((item) => {
    // Global search
    const matchesGlobal = Object.values(item).some((val) =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Column filters
    const matchesColumns = Object.keys(filters).every((key) => {
      if (!filters[key]) return true;
      return String(item[key] || "")
        .toLowerCase()
        .includes(filters[key].toLowerCase());
    });

    return matchesGlobal && matchesColumns;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    // Handle numeric values
    const aNum = Number(aVal);
    const bNum = Number(bVal);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
    }

    // Handle strings
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();

    if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
    if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const totals = sortedData.reduce(
    (acc, item) => {
      acc.currentWeight += Number(item.currentWeight || 0);
      acc.orderWeight += Number(item.orderWeight || 0);
      return acc;
    },
    { currentWeight: 0, orderWeight: 0 }
  );

  const fetchDieMasterData = async () => {
    setLoading(true);
    try {
      const res = await dieMasterAPI.getAllDieMaster(currentPage, pageSize);
      if (currentPage === 1) {
        setIsDieMasterData(res.data || []);
      } else {
        setIsDieMasterData((prev) => [...prev, ...(res.data || [])]);
      }

      if (!res.data || res.data.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      isFetchingRef.current = false;
    } catch (error) {
      notify("Error fetching die master data", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchDieMasterData();
  }, [currentPage]);

  const handleAdd = () => {
    setIsEditing(false);
    setFormData({ ...blankDieForm, created_By: userId, dieDate: today });
    setIsModelOpen(true);
  };

  const handleEdit = async (id) => {
    setLoading(true);
    try {
      const data = await dieMasterAPI.getById(id);

      if (data.data) {
        setFormData({
          id: data.data.id,
          dieNumber: data.data.dieNumber,
          dieDate: data.data.dieDate,
          subDieNo: data.data.subDieNo,
          invoiceNo: data.data.invoice_No,
          profileName: data.data.profile_Name,
          seriesName: data.data.series_Name,
          customer_Id: data.data.customer_Id,
          widthHeight: data.data.width_Height,
          currentWeight: data.data.currentWeight,
          orderWeight: data.data.orderWeight,
          status_Id: data.data.status_Id,
          supplier_Id: data.data.supplier_Id,
          location_Id: data.data.location_Id,
          range: data.data.to_Kg ?? data.data.To_Kg ?? "",
          range_Per: data.data.range_Per,
          die_Pdf: data.data.die_Pdf,
          die_Image: data.data.die_Image,
          toolingSize_Id: data.data.tooling_Size,
          die_Type_Id: data.data.die_Type_Id,
          no_Of_Cavity: data.data.no_Of_Cavity,
          hardness: data.data.hardness,
          area_Sqmm: data.data.area_Sqmm,
          perimeter: data.data.perimeter,
          profileThickness: data.data.profile_Thickness,
          created_By: userId,
        });

        setIsEditing(true);
        setIsModelOpen(true);
      }
    }
    catch (error) {
      notify("Failed to fetch die master details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await dieMasterAPI.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchDieMasterData();
    } catch (error) {
      notify("Failed to delete record", "error");
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleSubmit = async (submittedData, type) => {
    setLoading(true);
    try {
      await dieMasterAPI.saveOrUpdate(submittedData);
      notify("Saved successfully", "success");

      if (type === "blank") {
        setFormData({
          ...blankDieForm,
          created_By: userId,
          dieDate: today,
        });
      } else if (type === "sameInvoice") {
        setFormData({
          ...blankDieForm,
          invoiceNo: submittedData.get("Invoice_No"),
          supplier_Id: submittedData.get("Supplier_Id"),
          created_By: userId,
          dieDate: today,
        });
      } else {
        setIsModelOpen(false);
        setFormData(blankDieForm);
      }

      fetchDieMasterData();
    } catch (err) {
      notify("Save failed", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  if (isModelOpen) {
    return (
      <DieForm
        initialData={formData}
        key={JSON.stringify(formData)}
        onCancel={() => {
          setIsModelOpen(false);
          setFormData(blankDieForm);
        }}
        onSubmit={handleSubmit}
        isEditing={isEditing}
        isSaving={loading}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-white font-plus relative">
      {loading && <Loader centered />}
      <RegistryHeader title="Die Management">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsTourOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 h-[42px] text-[10px] font-black uppercase tracking-widest text-primary hover:bg-primary/5 transition-colors border border-primary bg-white shrink-0"
            title="Page Guide"
          >
            <Info size={14} />
            Guide
          </button>
        </div>
        <div className="relative w-full sm:w-80 group" data-tour="die-search">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-primary">
            <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
          </div>
          <input
            type="text"
            className="block w-full pl-12 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-primary focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
            placeholder="PROBE ASSET DATABASE..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
            >
              <MdClose size={18} />
            </button>
          )}
        </div>

        <div data-tour="die-add">
          <SharedButton
            onClick={handleAdd}
            icon={HiOutlinePlus}
            className="whitespace-nowrap"
          >
            Add Die
          </SharedButton>
        </div>
        {selectedIds.length > 0 && (
          <div data-tour="die-export">
            <SharedButton
              onClick={handleDownloadPDFs}
              className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
            >
              Download PDF ({selectedIds.length})
            </SharedButton>
          </div>
        )}
      </RegistryHeader>

      <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col" data-tour="die-table">
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-gray-200 bg-white flex-1"
        >
          <div className="min-w-max">
            <Table
              columns={[
                { key: "dieNumber", label: "Die No.", sticky: true, left: 40, w: "min-w-[120px]" },
                { key: "subDieNo", label: "SubDie No.", sticky: true, left: 160, w: "min-w-[150px]" },
                { key: "invoice_No", label: "Invoice No." },
                { key: "profile_Name", label: "Profile" },
                { key: "series_Name", label: "Series" },
                { key: "customer_Name", label: "Customer" },
                { key: "width_Height", label: "Width * Height (mm)" },
                {
                  key: "status_Name",
                  label: "Status",
                  render: (val) => {
                    const color = STATUS_COLORS[val] || "bg-gray-100 text-gray-600 border-gray-200";
                    const cleanColor = color.replace(/rounded-full|rounded-md|rounded-lg/g, 'rounded-none');
                    return (
                      <span className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-[2px] border rounded-none ${cleanColor}`}>
                        {val}
                      </span>
                    );
                  },
                },
                { key: "die_Range", label: "Range" },
                { key: "currentWeight", label: "Current Wt/Qyt (Kg)" },
                { key: "orderWeight", label: "Order Wt/Qyt (Kg)" },
                { key: "supplier_Name", label: "Supplier" },
                { key: "location_Name", label: "Location" },
                {
                  key: "die_Pdf",
                  label: "Pdf",
                  render: (value) =>
                    value ? (
                      <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary font-black hover:underline text-[10px] uppercase tracking-tighter"
                      >
                        View File
                      </a>
                    ) : (
                      <span className="text-gray-300 font-bold text-[9px] uppercase tracking-widest leading-none">NIL</span>
                    ),
                },
                {
                  key: "created_Date",
                  label: "Created Date",
                  render: (value) => <span className="text-gray-400 font-black tracking-tight">{formatDate(value)}</span>,
                },
                {
                  key: "modified_Date",
                  label: "Modified Date",
                  render: (value) => <span className="text-gray-400 font-black tracking-tight">{formatDate(value)}</span>,
                },
              ]}
              data={sortedData}
              filters={filters}
              onFilterChange={handleFilterChange}
              sortConfig={sortConfig}
              onSort={handleSort}
              checkbox={{
                selected: selectedIds,
                onToggle: handleToggle,
                onToggleAll: handleToggleAll,
                idField: "id",
              }}

              onEdit={(row) => handleEdit(row.id)}
              onDelete={(row) => handleDelete(row.id)}
              tableClassName="text-[12px] font-bold"
            />
          </div>
        </div>
      </div>

      <CalculationFooter
        items={[
          {
            label: "Total Die",
            value: sortedData.length.toLocaleString(),
            suffix: "PCS"
          },

        ]}
      />
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}

      <TourGuide
        isOpen={isTourOpen}
        onClose={() => setIsTourOpen(false)}
        steps={DieManagement_DieMasterTourSteps}
        onComplete={handleTourComplete}
      />
    </div>
  );
};

export default DieMaster;
